import React, { useState, useEffect } from "react";
import { DateTime } from "luxon";
import api from "../../api";
import "./RightDetails.css";
import "./SalesDetailsPanel.css";

const SalesDetails = ({ data, onClose, onSave, showToast = (msg, type) => console.log(msg, type) }) => {
  const [loading, setLoading] = useState(true);
  const [saleData, setSaleData] = useState(null);
  const [products, setProducts] = useState([]);
  const [saleItems, setSaleItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [personStatus, setPersonStatus] = useState("");
  const [deletingItemIds, setDeletingItemIds] = useState([]);
  const [newItemForm, setNewItemForm] = useState({
    product_id: "",
    quantity: 1,
    unit_price: 0,
    assigned_to: ""
  });
  
  // Fetch sale data, products and users
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // If we have a sale ID, fetch existing sale data
        if (data && data.id) {
          const saleResponse = await api.get(`/sales/${data.id}`);
          setSaleData(saleResponse.data);
          
          // Fetch sale items if they exist
          if (saleResponse.data.items) {
            setSaleItems(saleResponse.data.items);
          }
          
          // Fetch person's status if we have a person_id
          if (saleResponse.data.person_id) {
            try {
              const personResponse = await api.get(`/people/${saleResponse.data.person_id}`);
              setPersonStatus(personResponse.data.status || "");
            } catch (error) {
              console.error("Error fetching person data:", error);
            }
          }
        } else {
          // Initialize with empty data if no sale exists
          setSaleData({
            person_id: data?.person_id,
            person_name: data?.person_name || data?.name,
            company_id: data?.company_id,
            company_name: data?.company_name || data?.company,
            transaction_date: DateTime.local().toISO(),
            total_amount: 0,
            notes: ""
          });
          setSaleItems([]);
          
          // Fetch person's status if we have a person_id
          if (data?.person_id) {
            try {
              const personResponse = await api.get(`/people/${data.person_id}`);
              setPersonStatus(personResponse.data.status || "");
            } catch (error) {
              console.error("Error fetching person data:", error);
            }
          }
        }
        
        // Fetch products for selection
        const productsResponse = await api.get("/products");
        setProducts(productsResponse.data);
        
        // Fetch users for attribution
        const usersResponse = await api.get("/userinfo");
        setUsers(usersResponse.data);
        
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        showToast && showToast("Error loading sale details", "error");
        setLoading(false);
      }
    };
    
    fetchData();
  }, [data, showToast]);
  
  // Calculate total amount when items change
  useEffect(() => {
    if (saleItems.length > 0) {
      const total = saleItems.reduce((sum, item) => {
        const itemTotal = (item.quantity * item.unit_price) - (item.discount || 0);
        return sum + itemTotal;
      }, 0);
      
      setSaleData(prev => ({
        ...prev,
        total_amount: total
      }));
    }
  }, [saleItems]);
  
  const handleSaleChange = (field, value) => {
    setSaleData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  const handleItemChange = (index, field, value) => {
    setSaleItems(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: value
      };
      
      // Recalculate total price if quantity or unit_price changes
      if (field === "quantity" || field === "unit_price") {
        const quantity = field === "quantity" ? value : updated[index].quantity;
        const unitPrice = field === "unit_price" ? value : updated[index].unit_price;
        updated[index].total_price = (quantity * unitPrice) - (updated[index].discount || 0);
      }
      
      return updated;
    });
  };
  
  const addItem = () => {
    // Find the selected product to get its price
    const selectedProduct = products.find(p => p.id === parseInt(newItemForm.product_id));
    const unitPrice = selectedProduct ? selectedProduct.item_amount : 0;
    
    const newItem = {
      ...newItemForm,
      unit_price: newItemForm.unit_price || unitPrice,
      total_price: (newItemForm.quantity * (newItemForm.unit_price || unitPrice)),
      product_name: selectedProduct ? selectedProduct.sku_name : "Unknown Product"
    };
    
    setSaleItems(prev => [...prev, newItem]);
    
    // Reset form for next item
    setNewItemForm({
      product_id: "",
      quantity: 1,
      unit_price: 0,
      assigned_to: newItemForm.assigned_to // Keep the same rep selected
    });
  };
  
  const removeItem = async (index) => {
    const itemToRemove = saleItems[index];
    
    // If the item has an ID, it exists in the database and needs to be deleted
    if (itemToRemove.id && data && data.id) {
      try {
        // Mark this item as being deleted
        setDeletingItemIds(prev => [...prev, itemToRemove.id]);
        
        // Call the backend API to delete the item
        const response = await api.delete(`/sales/${data.id}/items/${itemToRemove.id}`);
        console.log("Item deleted from database:", response.data);
        
        // Update the total amount in the sale data
        setSaleData(prev => ({
          ...prev,
          total_amount: (prev.total_amount || 0) - (itemToRemove.total_price || 0)
        }));
        
        // Remove the item from the local state
        setSaleItems(prev => prev.filter((_, i) => i !== index));
        
        showToast && showToast("Item removed successfully", "success");
      } catch (error) {
        console.error("Error deleting item:", error);
        showToast && showToast(`Error deleting item: ${error.response?.data?.error || error.message}`, "error");
      } finally {
        // Remove this item from the deleting state regardless of success or failure
        setDeletingItemIds(prev => prev.filter(id => id !== itemToRemove.id));
      }
    } else {
      // Item doesn't exist in database, just remove it from local state
      setSaleItems(prev => prev.filter((_, i) => i !== index));
    }
  };
  
  const handleSaveAll = async () => {
    if (!saleData) return;
    
    try {
      setLoading(true);
      
      // Map items to correct format with explicit type conversion
      const itemsToSave = saleItems.map(item => ({
        product_id: parseInt(item.product_id),
        quantity: parseInt(item.quantity || 1),
        unit_price: parseFloat(item.unit_price || 0),
        assigned_to: item.assigned_to ? parseInt(item.assigned_to) : null,
        // Add safety checks for discount
        discount: item.discount ? parseFloat(item.discount) : 0
      }));
      
      // Construct payload with strict type validation
      const payload = {
        person_id: parseInt(saleData.person_id),
        company_id: saleData.company_id ? parseInt(saleData.company_id) : null,
        total_amount: parseFloat(saleData.total_amount || 0),
        notes: saleData.notes || '',
        created_by: saleData.created_by ? parseInt(saleData.created_by) : null,
        items: itemsToSave
      };
      
      console.log("Saving sale with payload:", payload);
      
      let response;
      let url;
      
      if (data && data.id) {
        // Update existing sale
        url = `/sales/${data.id}`;
        console.log(`Updating sale ID: ${data.id}, URL: ${url}`);
        response = await api.put(url, payload);
      } else {
        // Create new sale
        url = '/sales';
        console.log(`Creating new sale, URL: ${url}`);
        response = await api.post(url, payload);
      }
      
      console.log("Save response:", response.data);
      
      showToast && showToast(
        data && data.id ? "Sale updated successfully" : "Sale created successfully", 
        "success"
      );
      
      if (onSave) {
        // Return a simple object to prevent serialization issues
        onSave({
          id: data?.id || response.data?.id,
          message: "Sale saved successfully"
        });
      }
      
      setLoading(false);
    } catch (error) {
      console.error("Error saving sale:", error);
      console.error("Error details:", error.response?.data);
      showToast && showToast(`Error saving sale: ${error.response?.data?.error || error.message}`, "error");
      setLoading(false);
      // Don't call onSave on error
    }
  };

  const createdAt = saleData?.created_at ? new Date(saleData.created_at) : null;
  const updatedAt = saleData?.updated_at ? new Date(saleData.updated_at) : null;

  if (loading || !saleData) {
    return (
      <div className="right-details-container">
        <div className="right-details-topbar">
          <span className="page-chip">Sale Details</span>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        <div className="loading-container">Loading sale details...</div>
      </div>
    );
  }
  
  return (
    <div className="right-details-container">
      <div className="right-details-topbar">
        <span className="page-chip">Sale Details</span>
        <button className="close-button" onClick={onClose}>×</button>
      </div>
      
      <div className="right-details-header">
        <h2 className="sale-title">
          {data && data.id ? `Sale #${data.id}` : "New Sale"}
        </h2>
        <div className="sale-subtitle">
          {saleData.person_name || "Unknown Person"} - {saleData.company_name || "No Company"}
        </div>
        <div className="date-info">
          {createdAt && <div className="created-at">Created {createdAt.toLocaleString()}</div>}
          {updatedAt && <div className="updated-at">Last updated {updatedAt.toLocaleString()}</div>}
        </div>
      </div>
      
      <div className="right-details-form sale-details-form">
        <div className="sale-basic-info">
          <div className="form-group">
            <label>Transaction Date</label>
            <input 
              type="date" 
              value={saleData.transaction_date ? DateTime.fromISO(saleData.transaction_date).toFormat('yyyy-MM-dd') : ''} 
              onChange={(e) => handleSaleChange('transaction_date', e.target.value)}
            />
          </div>
          
          <div className="form-group">
            <label>Person Status</label>
            <div className="status-display">
              {personStatus ? (
                <span 
                  className="status-badge"
                  style={{
                    backgroundColor: getStatusColor(personStatus),
                    color: '#fff',
                    padding: '5px 10px',
                    borderRadius: '4px',
                    fontWeight: 'bold',
                    display: 'inline-block'
                  }}
                >
                  {personStatus}
                </span>
              ) : "Not Available"}
            </div>
          </div>
          
          <div className="form-group">
            <label>Notes</label>
            <textarea 
              value={saleData.notes || ''} 
              onChange={(e) => handleSaleChange('notes', e.target.value)}
              rows={3}
            />
          </div>
        </div>
        
        <div className="sale-items-section">
          <h3>Sale Items</h3>
          
          {saleItems.length === 0 ? (
            <div className="no-items-message">No items added to this sale yet</div>
          ) : (
            <table className="sale-items-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Quantity</th>
                  <th>Price</th>
                  <th>Total</th>
                  <th>Attributed To</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {saleItems.map((item, index) => (
                  <tr key={index}>
                    <td>{item.product_name || item.sku_name || "Unknown"}</td>
                    <td>
                      <input 
                        type="number" 
                        value={item.quantity} 
                        onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 1)}
                        min="1"
                      />
                    </td>
                    <td>
                      <input 
                        type="number" 
                        value={item.unit_price} 
                        onChange={(e) => handleItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
                        step="0.01"
                        min="0"
                      />
                    </td>
                    <td>${(item.total_price || (item.quantity * item.unit_price)).toFixed(2)}</td>
                    <td>
                      <select 
                        value={item.assigned_to || ""} 
                        onChange={(e) => handleItemChange(index, 'assigned_to', e.target.value)}
                      >
                        <option value="">Not Assigned</option>
                        {users.map(user => (
                          <option key={user.id} value={user.id}>
                            {user.first_name} {user.last_name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <button 
                        className="remove-item-btn" 
                        onClick={() => removeItem(index)}
                        disabled={deletingItemIds.includes(item.id)}
                      >
                        {deletingItemIds.includes(item.id) ? "⌛" : "✕"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="3" className="total-label">Total Amount:</td>
                  <td colSpan="3" className="total-value">${saleData.total_amount.toFixed(2)}</td>
                </tr>
              </tfoot>
            </table>
          )}
          
          <div className="add-item-form">
            <h4>Add New Item</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Product</label>
                <select 
                  value={newItemForm.product_id} 
                  onChange={(e) => setNewItemForm(prev => ({ ...prev, product_id: e.target.value }))}
                >
                  <option value="">Select Product</option>
                  {products.map(product => (
                    <option key={product.id} value={product.id}>
                      {product.sku_name} (${product.item_amount})
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Quantity</label>
                <input 
                  type="number" 
                  value={newItemForm.quantity} 
                  onChange={(e) => setNewItemForm(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                  min="1"
                />
              </div>
              
              <div className="form-group">
                <label>Price Override</label>
                <input 
                  type="number" 
                  value={newItemForm.unit_price} 
                  onChange={(e) => setNewItemForm(prev => ({ ...prev, unit_price: parseFloat(e.target.value) || 0 }))}
                  step="0.01"
                  placeholder="Leave empty for default"
                />
              </div>
              
              <div className="form-group">
                <label>Attributed To</label>
                <select 
                  value={newItemForm.assigned_to} 
                  onChange={(e) => setNewItemForm(prev => ({ ...prev, assigned_to: e.target.value }))}
                >
                  <option value="">Not Assigned</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.first_name} {user.last_name}
                    </option>
                  ))}
                </select>
              </div>
              
              <button 
                className="add-item-btn" 
                onClick={addItem}
                disabled={!newItemForm.product_id}
              >
                Add Item
              </button>
            </div>
          </div>
        </div>
        
        <div className="right-details-actions">
          <button className="cancel-btn" onClick={onClose}>Cancel</button>
          <button 
            className="save-btn" 
            onClick={handleSaveAll}
            disabled={loading || saleItems.length === 0}
          >
            {loading ? "Saving..." : "Save Sale"}
          </button>
        </div>
      </div>
    </div>
  );
};

// Helper function to get status color
const getStatusColor = (status) => {
  switch (status) {
    case "Closed Won":
      return "#4CAF50"; // Green
    case "Closed Lost":
      return "#F44336"; // Red
    case "Qualified":
      return "#8BC34A"; // Light Green
    case "New":
      return "#2196F3"; // Blue
    case "Contacted":
      return "#FFC107"; // Amber
    case "Proposal Sent":
      return "#FF9800"; // Orange
    default:
      return "#9E9E9E"; // Grey
  }
};

export default SalesDetails; 